import {
	Body,
	Button,
	Checkbox,
	dsColors,
	FlexBox,
	FormControl,
	FormError,
	Headline,
	Icon,
	Input,
	Message,
	Select,
	BlcLoader,
} from '@Backlot-Cars/archie';
import { Trans, useTranslation } from 'next-i18next';
import SignUpPolicies from 'partials/sign_up/policies';
import { Controller, useForm } from 'react-hook-form';
import { AuctionAccessAccountDetail } from '../shared/AuctionAccessAccountDetail';
import { FALLBACK_PRIVACY_POLICY, formatAddress, formatAuctionAccessId } from '../Lookup/helpers';
import { LookupDetailsGrid } from '../shared/styles';
import { ReviewActions, ReviewContent, ReviewHeader, ReviewHeaderRow, ReviewPolicies, ReviewSection } from './styles';
import useAuctionAccessCompanyValidation, { EIN_LAST_FOUR_LENGTH } from './useAuctionAccessCompanyValidation';

const COMPANY_CANDIDATE_ID_FIELD = 'company_candidate_id';
const LAST4_GOVERNMENT_ID_FIELD = 'last4GovernmentId';

const getAcceptedMarketplaceDocumentIds = (values, policies) =>
	(Array.isArray(policies) ? policies : [])
		.filter(
			({ document_type, marketplace_document_id }) =>
				document_type && marketplace_document_id != null && values?.[document_type]?.value,
		)
		.map(({ marketplace_document_id }) => marketplace_document_id);

const FallbackPolicyCheckbox = ({ formErrors, id, label, registration, required = false }) => (
	<FormControl formErrors={formErrors} label={label} labelPosition="right" labelWrap="normal" required={required}>
		<Checkbox id={id} {...registration} />
	</FormControl>
);

const AuctionAccessFallbackPolicies = ({ errors, register }) => {
	const { t } = useTranslation(['signup']);

	return (
		<ReviewPolicies>
			<FallbackPolicyCheckbox
				formErrors={errors}
				id="auction-access-privacy-policy"
				label={
					<Body>
						<Trans
							i18nKey="signup:auctionAccessLookup.verified.policies.privacy"
							t={t}
							components={[
								<Button
									href="/privacy_policy"
									key="privacy-policy"
									labelColor={dsColors.colorBrand.primary}
									variant="link"
								/>,
							]}
						/>
					</Body>
				}
				registration={register(FALLBACK_PRIVACY_POLICY, {
					required: t('signup:steps.step0.privacyPolicyRequired'),
				})}
				required
			/>
			<FallbackPolicyCheckbox
				formErrors={errors}
				id="auction-access-email-notifications"
				label={<Body>{t('signup:auctionAccessLookup.verified.policies.email')}</Body>}
				registration={register('auctionAccessEmailNotifications')}
			/>
			<Body>
				<Trans
					i18nKey="signup:auctionAccessLookup.verified.policies.smsPolicy"
					t={t}
					components={[
						<Button
							href="/sms_terms"
							key="sms-terms"
							labelColor={dsColors.colorBrand.primary}
							variant="link"
						/>,
					]}
				/>
			</Body>
			<FallbackPolicyCheckbox
				formErrors={errors}
				id="auction-access-sms-account-activity"
				label={<Body>{t('signup:auctionAccessLookup.verified.policies.smsAccountActivity')}</Body>}
				registration={register('auctionAccessSmsAccountActivity')}
			/>
			<FallbackPolicyCheckbox
				formErrors={errors}
				id="auction-access-sms-support"
				label={<Body>{t('signup:auctionAccessLookup.verified.policies.smsSupport')}</Body>}
				registration={register('auctionAccessSmsSupport')}
			/>
		</ReviewPolicies>
	);
};

const AuctionAccessVerified = ({
	account,
	error,
	initialValues = {},
	isReviewSubmitting = false,
	onCancel,
	onNext,
	signUpPolicies = [],
}) => {
	const { t } = useTranslation(['signup']);
	const {
		handleSubmit,
		control,
		register,
		setError,
		setValue,
		watch,
		formState: { errors, isSubmitting },
	} = useForm({ defaultValues: initialValues });
	const policies = Array.isArray(signUpPolicies) ? signUpPolicies : [];
	const last4GovernmentId = watch(LAST4_GOVERNMENT_ID_FIELD);
	const policyValues = policies
		.filter(({ document_type }) => document_type)
		.map(({ document_type }) => watch(document_type))
		.filter(Boolean);
	const dealerships = Array.isArray(account?.dealerships) ? account.dealerships : [];
	const hasDealershipPicker = dealerships.length > 1;
	const selectedDealershipCompanyCandidateId = watch(COMPANY_CANDIDATE_ID_FIELD) || '';
	const selectedDealership =
		hasDealershipPicker ?
			dealerships.find(
				({ companyCandidateId }) => String(companyCandidateId || '') === selectedDealershipCompanyCandidateId,
			) || {}
		:	dealerships[0] || {};
	const selectedCompanyCandidateId = String(
		selectedDealershipCompanyCandidateId || selectedDealership.companyCandidateId || '',
	);
	const handleContinueManually = () =>
		onCancel?.({
			dealershipName: selectedDealership.name,
			email: account?.email,
			firstName: account?.firstName,
			lastName: account?.lastName,
			mobilePhone: account?.mobilePhone,
		});
	const {
		error: validationError,
		isLoading: isValidatingCompany,
		validatedDealership,
	} = useAuctionAccessCompanyValidation({
		companyCandidateId: selectedCompanyCandidateId,
		ein: last4GovernmentId,
	});

	const handleReviewSubmit = async (values) => {
		if (!selectedCompanyCandidateId || !validatedDealership) {
			setError(LAST4_GOVERNMENT_ID_FIELD, {
				type: 'validate',
				message: t('signup:auctionAccessLookup.verified.einValidationError'),
			});
			return;
		}

		const missingRequiredPolicies = policies.filter(
			({ acceptance_required, document_type }) =>
				acceptance_required && document_type && !values[document_type]?.value,
		);

		if (missingRequiredPolicies.length) {
			missingRequiredPolicies.forEach(({ document_type }) => {
				setError(document_type, {
					type: 'required',
					message: t('signup:steps.step0.privacyPolicyRequired'),
				});
			});
			return;
		}

		const marketplaceDocumentIds = getAcceptedMarketplaceDocumentIds(values, policies);

		return onNext?.(
			{
				...values,
				[COMPANY_CANDIDATE_ID_FIELD]: selectedCompanyCandidateId,
				marketplaceDocumentIds,
			},
			validatedDealership.type,
		);
	};

	const formErrors =
		validationError ?
			{
				...errors,
				[LAST4_GOVERNMENT_ID_FIELD]: {
					type: 'validate',
					message: t('signup:auctionAccessLookup.verified.einValidationError'),
				},
			}
		:	errors;
	const hasEinValidationError = Boolean(validationError || errors[LAST4_GOVERNMENT_ID_FIELD]?.type === 'validate');
	const einFormErrors =
		hasEinValidationError ? { ...formErrors, [LAST4_GOVERNMENT_ID_FIELD]: undefined } : formErrors;

	return (
		<form onSubmit={handleSubmit(handleReviewSubmit)}>
			<ReviewContent>
				<ReviewHeader>
					<ReviewHeaderRow>
						<Icon color="secondary" name="check" size="normal" />
						<Headline size="H2">{t('signup:auctionAccessLookup.verified.title')}</Headline>
					</ReviewHeaderRow>
					<Body>{t('signup:auctionAccessLookup.verified.subTitle')}</Body>
				</ReviewHeader>
				<ReviewSection>
					<Headline size="H5">{t('signup:auctionAccessLookup.verified.accountInformation')}</Headline>
					<LookupDetailsGrid>
						<FlexBox flexDirection="column" gap="md">
							<AuctionAccessAccountDetail
								label={t('signup:auctionAccessLookup.verified.name')}
								value={account?.name}
							/>
							<AuctionAccessAccountDetail
								label={t('signup:auctionAccessLookup.verified.mobilePhone')}
								value={account?.mobilePhone}
							/>
						</FlexBox>
						<FlexBox flexDirection="column" gap="md">
							<AuctionAccessAccountDetail
								label={t('signup:auctionAccessLookup.verified.email')}
								value={account?.email}
							/>
							<AuctionAccessAccountDetail
								label={t('signup:auctionAccessLookup.verified.auctionAccessId')}
								value={formatAuctionAccessId(account?.auctionAccessId)}
							/>
						</FlexBox>
					</LookupDetailsGrid>
				</ReviewSection>
				<ReviewSection>
					<Headline size="H5">{t('signup:auctionAccessLookup.verified.dealershipInformation')}</Headline>
					<FormControl
						formErrors={einFormErrors}
						label={t('signup:auctionAccessLookup.verified.ein')}
						required
					>
						<Input
							id={LAST4_GOVERNMENT_ID_FIELD}
							inputMode="numeric"
							maxLength={EIN_LAST_FOUR_LENGTH}
							pattern="[0-9]{4}"
							disabled={isValidatingCompany}
							placeholder={t('signup:auctionAccessLookup.verified.einPlaceholder')}
							{...register(LAST4_GOVERNMENT_ID_FIELD, {
								required: t('signup:auctionAccessLookup.verified.einRequired'),
								pattern: {
									value: /^[0-9]{4}$/,
									message: t('signup:auctionAccessLookup.verified.einRequired'),
								},
							})}
						/>
					</FormControl>
					{hasEinValidationError && (
						<FlexBox alignItems="center" gap="xxs">
							<FormError text={t('signup:auctionAccessLookup.verified.einValidationError')} />
							<Body>
								<Trans
									i18nKey="signup:auctionAccessLookup.verified.einValidationErrorAction"
									t={t}
									components={[
										<Button
											key="continue-manually"
											labelColor={dsColors.colorBrand.primary}
											onClick={handleContinueManually}
											variant="link"
										/>,
									]}
								/>
							</Body>
						</FlexBox>
					)}
					{isValidatingCompany && (
						<FlexBox justifyContent="center">
							<BlcLoader />
						</FlexBox>
					)}
					{hasDealershipPicker && (
						<>
							<Message borderColor="secondary" color="secondary" iconColor="secondary" iconName="info">
								<FlexBox flexDirection="column" gap="xxs">
									<Body bold>
										{t('signup:auctionAccessLookup.verified.multipleDealershipsFound')}
									</Body>
									<Body>
										{t('signup:auctionAccessLookup.verified.multipleDealershipsDescription')}
									</Body>
								</FlexBox>
							</Message>
							<FormControl
								formErrors={errors}
								inputName={COMPANY_CANDIDATE_ID_FIELD}
								label={t('signup:auctionAccessLookup.verified.dealershipSelectLabel')}
								required
							>
								<Controller
									control={control}
									name={COMPANY_CANDIDATE_ID_FIELD}
									rules={{
										required: t('signup:auctionAccessLookup.verified.dealershipSelectRequired'),
									}}
									render={({ field: { name, onBlur, onChange, ref, value } }) => (
										<Select
											id={name}
											name={name}
											onChange={onChange}
											onBlur={onBlur}
											options={dealerships.map(({ companyCandidateId, name }) => ({
												value: String(companyCandidateId || ''),
												text: name,
											}))}
											placeholder={t(
												'signup:auctionAccessLookup.verified.dealershipSelectPlaceholder',
											)}
											value={value || ''}
											ref={ref}
										/>
									)}
								/>
							</FormControl>
						</>
					)}
					{Boolean(validatedDealership) && (
						<LookupDetailsGrid>
							<FlexBox flexDirection="column" gap="md">
								<AuctionAccessAccountDetail
									label={t('signup:auctionAccessLookup.verified.dealershipName')}
									value={validatedDealership.name}
								/>
								<AuctionAccessAccountDetail
									label={t('signup:auctionAccessLookup.verified.dealershipAddress')}
									value={formatAddress(validatedDealership.address)}
								/>
								<AuctionAccessAccountDetail
									label={t('signup:auctionAccessLookup.verified.dealerLicense')}
									value={validatedDealership.dealerLicense?.number}
								/>
							</FlexBox>
							<FlexBox flexDirection="column" gap="md">
								<AuctionAccessAccountDetail
									label={t('signup:auctionAccessLookup.verified.dealershipType')}
									value={validatedDealership.type}
								/>
								<AuctionAccessAccountDetail
									label={t('signup:auctionAccessLookup.verified.dealershipPhone')}
									value={validatedDealership.phoneNumber}
								/>
							</FlexBox>
						</LookupDetailsGrid>
					)}
				</ReviewSection>
				{policies.length > 0 ?
					<ReviewPolicies>
						<SignUpPolicies
							policies={policies}
							policyValues={policyValues}
							smsPolicyCopy={t('signup:steps.step0.smsPolicy')}
							smsLinkCopy={t('signup:steps.step0.smsLink')}
							errors={errors}
							register={register}
							setValue={setValue}
							syncPolicyValues={false}
						/>
					</ReviewPolicies>
				:	<AuctionAccessFallbackPolicies errors={errors} register={register} />}
				<FormError text={error} />
				<ReviewActions>
					<Button
						disabled={!onNext || isSubmitting || isReviewSubmitting || isValidatingCompany}
						type="submit"
					>
						{t('signup:actions.next')}
					</Button>
					<Button disabled={isReviewSubmitting} variant="tertiary" onClick={() => onCancel?.()}>
						{t('signup:auctionAccessLookup.verified.cancel')}
					</Button>
				</ReviewActions>
			</ReviewContent>
		</form>
	);
};

export default AuctionAccessVerified;
